import { Avatar, AvatarGroup, Box, Button, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';
import hljs from 'highlight.js';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import ConfirmDialog from '../../components/ConfirmDialog';
import { SocialShare } from '../../components/FBSocial';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
import Markdown from '../../components/Markdown';
import Scrollbar from '../../components/Scrollbar';
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import { deleteSolutionAPI } from '../../service/hackerthon/hackerthon.solution.service';
import { getStatisticAPI, updateLikeAPI } from '../../service/hackerthon/hackerthon.statistic.service';
import { fShortenNumber } from '../../utils/formatNumber';
import CommentList from '../comment/CommentList';
import CommentNewForm from '../comment/CommentNewForm';
import SolutionCreate from './SolutionCreate';

export default function SolutionDetail() {
  const router = useRouter();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAuthenticated } = useAuth();

  const { solution } = useSelector((state) => state.hackerthonSolution);
  const [editable, setEditable] = useState(false);
  const [statistic, setStatistic] = useState({ viewNo: 0, likeNo: 0, commentNo: 0, likers: [] });

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getStatistic();
      hljs.highlightAll();
    }, 500);
    return () => clearTimeout(timeout);
  }, [solution?.id]);

  const close = () => {
    router.back();
  };

  const confirmDeleteItem = async () => {
    let resp = await deleteSolutionAPI(solution?.id);

    setOpen(false);
    if (resp.code === "200") {
      enqueueSnackbar(translate('message.deleteSuccess'), { variant: 'success' });
      close();
    } else
      enqueueSnackbar(`${resp.code} - ${resp.message}`, { variant: 'error' });
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      enqueueSnackbar(translate('label.signinToJmaster'), { variant: 'error' });
      return;
    }
    const { code } = await updateLikeAPI(solution?.statistic.id);
    code === '200' && getStatistic();
  };

  const getStatistic = async () => {
    const { code, data } = await getStatisticAPI(solution?.statistic.id);
    code === '200' && setStatistic(data);
  };

  const handleEdit = async () => {
    setEditable(!editable);
  };

  if (editable)
    return <SolutionCreate solution={solution} isEdit={true} setMode={handleEdit} />;

  return (
    <Scrollbar sx={{ flexGrow: 1, p: 1 }}>
      <Stack mb={1} spacing={0.25}>
        <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"space-between"}>
          <Typography variant='subtitle1'>{solution?.title}</Typography>
          <Button size="small" onClick={close} startIcon={<Iconify icon="ep:back" width={18} height={18} />}>
            {translate("button.goBack")}
          </Button>
        </Stack>
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <SocialShare title={solution?.title} />
          {user?.id === solution?.createdBy?.id && (
            <Button onClick={handleEdit} size='small' color='primary' variant='text' startIcon={<Iconify icon="circum:edit" sx={{ height: 15, width: 15 }} />}>
              {translate("button.edit")}
            </Button>)}
          {user?.id === solution?.createdBy?.id && (
            <Button size='small' color='error' variant='text' startIcon={<Iconify icon="octicon:trash-16" sx={{ height: 15, width: 15 }} />}
              onClick={() => setOpen(true)}>
              {translate("button.delete")}
            </Button>)}
        </Stack>
        <Box>
          {solution?.tags?.map((tag) => (
            <Link key={tag.id} href={`/tag/${tag.slug}`} passHref>
              <Label sx={{ mr: 1, cursor: 'pointer' }}>{tag.title}</Label>
            </Link>
          ))}
        </Box>
      </Stack>
      <div className='ql-snow'>
        <div className='ql-editor' style={{ padding: 0 }}>
          <Markdown children={solution?.description} />
        </div>
      </div>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked
              onChange={handleLike}
              size="small"
              color="error"
              icon={<Iconify icon="eva:heart-fill" />}
              checkedIcon={<Iconify icon="eva:heart-fill" />}
            />
          }
          label={fShortenNumber(statistic?.likeNo)}
        />
        <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 22, height: 22 }, }} >
          {statistic?.likers?.map((person) => (
            <Avatar key={person.displayName} alt={person.displayName} src={person.photoURL} />
          ))}
        </AvatarGroup>
      </Box>
      <Divider />
      <CommentNewForm itemId={solution?.id} type={"SOLUTION"} />
      <CommentList />
      <ConfirmDialog values={{ title: translate("message.dialogDeleteTitle"), content: translate("message.dialogDeleteContent") }}
        onClose={() => setOpen(false)} isOpen={open} onSubmit={confirmDeleteItem} />
    </Scrollbar>
  );
}
